import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Building2, DollarSign, TrendingUp, LogOut, Mail, Loader2, Menu, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import founderApi, { FOUNDER_KEY, errMsg } from "@/lib/founderApi";
import { useConfirm } from "@/lib/confirm";

const NAV = [
  { to: "/founder/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/founder/companies", label: "Companies", icon: Building2 },
  { to: "/founder/revenue", label: "Earnings", icon: DollarSign },
  { to: "/founder/growth", label: "Acquisition", icon: TrendingUp },
];

export default function FounderLayout() {
  const nav = useNavigate();
  const ask = useConfirm();
  const [me, setMe] = useState(null);
  const [demo, setDemoState] = useState(() => localStorage.getItem("rs_founder_demo") === "1");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const setDemo = (v) => { setDemoState(v); localStorage.setItem("rs_founder_demo", v ? "1" : "0"); };

  useEffect(() => {
    document.title = "Founder · RideStamp";
    if (!sessionStorage.getItem(FOUNDER_KEY)) { nav("/founder", { replace: true }); return; }
    founderApi.get("/me").then((r) => setMe(r.data)).catch(() => nav("/founder", { replace: true }));
  }, [nav]);

  const logout = async () => {
    if (!(await ask({ title: "Sign out of the founder portal?", confirmLabel: "Sign out" }))) return;
    await founderApi.post("/logout").catch(() => {});
    sessionStorage.removeItem(FOUNDER_KEY);
    nav("/founder", { replace: true });
  };

  const digest = async () => {
    setSending(true);
    try { await founderApi.post("/platform/digest/send-now"); toast.success("Founder digest sent to your inbox."); }
    catch (e) { toast.error(errMsg(e, "Couldn't send the digest.")); } finally { setSending(false); }
  };

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-[#070b14]"><Loader2 className="h-6 w-6 animate-spin text-blue-400" /></div>;

  const links = NAV.map((n) => (
    <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`founder-nav-${n.label.toLowerCase()}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-blue-600/15 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"}`}>
      <n.icon className="h-4 w-4" /> {n.label}
    </NavLink>
  ));

  return (
    <div className="min-h-screen bg-[#070b14] text-white" data-testid="founder-layout">
      <aside className={`fixed inset-y-0 left-0 z-40 w-60 border-r border-slate-800 bg-[#0a101d] p-4 transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="mb-8 px-2 pt-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-400">RideStamp</p>
          <p className="font-display text-lg font-bold">Founder console</p>
        </div>
        <nav className="space-y-1">{links}</nav>
        <div className="absolute bottom-4 left-4 right-4 space-y-2 border-t border-slate-800 pt-4">
          <p className="truncate px-2 text-xs text-slate-500" data-testid="founder-me">{me.email}</p>
          <button onClick={logout} data-testid="founder-logout" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-white"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-800 bg-[#070b14]/85 px-4 py-3 backdrop-blur-xl sm:px-8">
          <button className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800 lg:hidden" onClick={() => setOpen(!open)} data-testid="founder-menu-toggle">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          <div className="ml-auto flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <Switch checked={demo} onCheckedChange={setDemo} data-testid="founder-demo-switch" /> Include demo companies
            </label>
            <button onClick={digest} disabled={sending} data-testid="founder-digest-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-800">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} <span className="hidden sm:inline">Email me a digest</span>
            </button>
          </div>
        </header>
        <main className="px-4 py-8 sm:px-8"><Outlet context={{ demo }} /></main>
      </div>
    </div>
  );
}
